import { zod } from '@acme/core'
import { Alert } from '@mui/material'
import TabbedDialog from 'global/guides/Dialog/TabbedDialog'
import { hasPermission } from 'hooks/useBackendMenu'
import React, { useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
    Funcionario,
    clientes,
    empresas as empresas_api,
    funcionario,
} from 'service/api'
import { toast } from 'sonner'
import { useStore } from 'store'
import { z } from 'zod'
import { AdicionaisForm } from '../forms/Adicionais'
import { DadosPessoaisForm } from '../forms/DadosPessoais'

const snSchema = z
    .boolean()
    .nullish()
    .transform(value => (value === true ? 'S' : value === false ? 'N' : null))

export const schema = z
    .object({
        id: z.number().optional(),
        matricula: z.coerce
            .number()
            .max(9999999999)
            .nullish()
            .transform(value => value || undefined),
        empresaId: z.coerce.number().min(1, 'Campo obrigatório'),
        nome: z.string().min(1, 'Campo obrigatório'),
        pis: z
            .string()
            .min(11, 'Campo obrigatório')
            .transform(value => value.replace(/\D/g, '')),
        regraCalculo: z.coerce.number().min(1, 'Campo obrigatório'),
        tabelaHorarioId: z.coerce.number().optional(),
        horarioNoturno: snSchema.default(false),
        entradaEntre231: snSchema.default(false),
        repSupervisor: snSchema.default(false),
        registroApp: snSchema.default(false),
        registroWeb: snSchema.default(false),
        ajustesPonto: snSchema.default(false),
        verSaldoHoras: snSchema.default(false),
        recebeValeAlimentacao: snSchema.default(true),
        expFolhaHorasExtras: snSchema.default(true),
        expFolhaFaltas: snSchema.default(true),
        expFolhaAdicionalNoturno: snSchema.default(true),
        expFolhaHorasNormais: snSchema.default(true),
        desconsiderarFeriados: snSchema.default(false),
        cpf: z
            .string()
            .min(11, 'Campo obrigatório')
            .transform(value => value.replace(/\D/g, '')),
        uf: z.string().min(2, 'Campo obrigatório'),
        bonusValeAlimentacao: z.coerce.number().min(0).nullish(),
        dtNascimento: z.coerce
            .date({
                invalid_type_error: 'Formato de data inválido',
            })
            .nullish(),
        funcao: z.string().default(''),
        dtAdmissao: z.coerce.date({ invalid_type_error: 'Campo obrigatório' }),
        dtRescisao: z
            .string()
            .or(z.date({ invalid_type_error: 'Campo obrigatório' }))
            .nullish()
            .optional()
            .transform(value => value || null),
        Cidade: zod.CidadeSchema.omit({
            id: true,
            createdAt: true,
            updatedAt: true,
        })
            .nullable()
            .refine(value => value !== null, {
                message: 'Cidade é obrigatória',
            }),
        FuncionarioLocais: zod.FuncionarioLocaisSchema.omit({
            createdAt: true,
        })
            .deepPartial()
            .array()
            .nullish(),
    })
    .passthrough()

export default function AdicionarFuncionarioPage() {
    const navigate = useNavigate()
    const [rascunho] = React.useState(() => getRascunho())
    const empresaId = useStore(state => state.companyId)
    const { data: empresas } = empresas_api.useFetch({ enabled: empresaId > 0 })
    const empresaUsaBancoHoras =
        (empresas?.find(empresa => empresa.id === empresaId)
            ?.periodoFechamentoPonto || 0) > 0
    const { data: limites } = clientes.limites.useFetch(false)
    const podeCadastrar = Number(limites?.ativos) < Number(limites?.limite)

    const { mutateAsync, isLoading } = funcionario.mutations.useCreate({
        invalidateQuery: true,
        onSuccess: () => {
            toast.success('Funcionario adicionado com sucesso!')
        },
        onError: err => {
            if (err.statusCode === 400) {
                toast.error(err.message)
            } else {
                toast.error('Erro ao adicionar funcionario!')
            }
        },
    })

    useEffect(() => {
        if (Object.values(rascunho).length) {
            localStorage.removeItem('@rascunho:funcionario')
        }
    }, [rascunho])

    return (
        <TabbedDialog.Root
            tabs={[
                {
                    name: 'dados',
                    label: 'Dados básicos',
                    content: <DadosPessoaisForm />,
                },
                {
                    name: 'adicionais',
                    label: 'Adicionais',
                    content: <AdicionaisForm />,
                },
            ]}
            open={hasPermission(['create', 'Funcionários'])}
            onSubmit={data => mutateAsync(data)}
            loading={isLoading}
            schema={schema}
            defaultValues={{
                ...rascunho,
                empresaId,
                empresaUsaBancoHoras,
                controlarBancoHoras: null,
            }}
            onClose={() => navigate('/cadastros/funcionarios')}
        >
            <TabbedDialog.Title>Adicionar funcionário</TabbedDialog.Title>
            <TabbedDialog.Content dividers>
                {!podeCadastrar && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        Você atingiu o limite de funcionários ativos. Para
                        adicionar mais,&nbsp;
                        <Link to="/planos/me">altere seu plano</Link>.
                    </Alert>
                )}
            </TabbedDialog.Content>
            <TabbedDialog.Actions>
                <TabbedDialog.Actions.SecondaryButton />
                <TabbedDialog.Actions.PrimaryButton
                    closeOnSubmit
                    disabled={!podeCadastrar}
                />
            </TabbedDialog.Actions>
        </TabbedDialog.Root>
    )
}

/**
 * Atualiza o rascunho do funcionário no localStorage
 * @param data informações a serem atualizadas no rascunho
 * @param force caso não exista um rascunho, um novo é criado com as informações passadas
 */
export function updateRascunho(
    data: Partial<Funcionario & { tabelaHorarioId?: number }>,
    force = false,
) {
    const rascunho = getRascunho()
    if (!force && !Object.values(rascunho).length) return
    localStorage.setItem(
        '@rascunho:funcionario',
        JSON.stringify({ ...rascunho, ...data }),
    )
}

type RascunhoFuncionario = Partial<Funcionario & { tabelaHorarioId: number }>
/**
 * Retorna o rascunho do funcionário no localStorage
 * @param fallback valor padrão caso não exista um rascunho
 */
function getRascunho(fallback: RascunhoFuncionario = {}): RascunhoFuncionario {
    const rascunho = localStorage.getItem('@rascunho:funcionario')
    return rascunho ? JSON.parse(rascunho) : fallback
}

export { getRascunho as getRascunhoFuncionario }
